(ns app.front.view.recently-viewed
  "Lists the entities the current user has visited most recently.

  The visits come from the `:user/recently-visited` command, which also returns
  the visited entities themselves for the frontend entity db."
  (:require ["@carbon/icons-react/es/grid/16" :default Grid16]
            ["@carbon/icons-react/es/home/16" :default Home16]
            ["@carbon/icons-react/es/identification/16" :default Identification16]
            ["@carbon/icons-react/es/inspection/16" :default Inspection16]
            ["@carbon/icons-react/es/license/16" :default License16]
            ["@carbon/icons-react/es/report/16" :default Report16]
            [app.domain.complaint.model :as complaint-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.property.model :as property-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.ui.results-tab-switcher :as ui-rts]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.updated :as ui-updated]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(def ^:private visit-limit 20)

(rf/reg-event-fx ::fetch
  (fn [_ [_ limit]]
    {:comm/command {:command    :user/recently-visited
                    :method     :get
                    :data       {:limit (or limit visit-limit)}
                    :on-success [::fetch-success]}}))

(rf/reg-event-fx ::fetch-success
  (fn [{:keys [db]} [_ status {:keys [visits entities]}]]
    {:db (assoc db ::visits visits)
     :fx [[:dispatch [::entities/merge-entity-db status entities]]]}))

(rf/reg-sub ::visits
  :-> ::visits)

(def ^:private entity-type->kind
  "Translation key naming the kind of a visited entity."
  {:entity.type/workspace               :workspace/workspace
   :entity.type/licence                 :licence/licence
   :entity.type/contractor-registration :contractor-registration/contractor-registration
   :entity.type/property                :property/property
   :entity.type/complaint               :complaint/complaint
   :entity.type/inspection              :inspection/inspection})

(def ^:private entity-type->icon
  "Icon shown next to the kind of a visited entity on its card."
  {:entity.type/workspace               Grid16
   :entity.type/licence                 License16
   :entity.type/contractor-registration Identification16
   :entity.type/property                Home16
   :entity.type/complaint               Report16
   :entity.type/inspection              Inspection16})

(defn- entity-open-tr-key
  "Translation key for the button opening the page of a visited entity."
  [entity]
  (case (:entity/type entity)
    :entity.type/workspace               (ws-util/workspace-link-description entity)
    :entity.type/licence                 :search/open-licence
    :entity.type/contractor-registration :search/open-contractor-registration
    :open))

(defmulti ^:private entity-labels
  "The name, description and address columns for a visited entity.

  Only attributes of the entity itself and of the few entities the command
  exports along with it can be used here, so e.g. licence categories are not
  available yet."
  (fn [_opts entity] (:entity/type entity)))

(defmethod entity-labels :default [_ _]
  nil)

(defmethod entity-labels :entity.type/workspace [{:keys [tr]} workspace]
  {:name        (workspace-model/case-id workspace)
   :description (some-> workspace :workspace/permit-type vector tr)
   :address     (:workspace/address workspace)})

(defmethod entity-labels :entity.type/licence [_ licence]
  {:name    (:licence/id licence)
   :address (:licence/address licence)})

(defmethod entity-labels :entity.type/contractor-registration [{:keys [lang]} registration]
  {:name (-> registration :contractor-registration/registrant-names (i18n-model/get-text lang))})

(defmethod entity-labels :entity.type/complaint [{:keys [lang]} complaint]
  {:name        (complaint-model/category-name complaint lang)
   :description (:complaint/description complaint)
   :address     (:complaint/address complaint)})

(defmethod entity-labels :entity.type/inspection [{:keys [lang]} inspection]
  {:name        (-> inspection :inspection/names (i18n-model/get-text lang))
   :description (-> inspection :inspection/visit-type (inspection-model/visit-type-name lang))
   :address     (-> inspection inspection-model/workspace :workspace/address)})

(defmethod entity-labels :entity.type/property [{:keys [tr]} property]
  {:name        (:property/id property)
   :description (property-model/property-type-text tr property)
   :address     (property-model/full-address property)})

(defmulti ^:private entity-route
  "Route name, path params and optional query params for the page of a visited
  entity, as a `[route-name path-params query-params?]` vector. Pure, so tests
  can exercise the route selection without stubbing `href/branded-href`."
  :entity/type)

(defmethod entity-route :default [_]
  nil)

(defmethod entity-route :entity.type/workspace [workspace]
  [(if (ws-util/resume-application? workspace)
     :view/application-wizard-resume
     :view/workspace)
   {:workspace-id (:workspace/id workspace)}])

(defmethod entity-route :entity.type/licence [licence]
  (when-some [group-id (-> licence :domain/authority-group :group/id)]
    [:view/licence {:group-id   group-id
                    :licence-id (:licence/id licence)}]))

(defmethod entity-route :entity.type/contractor-registration [registration]
  [:view/contractor-registration {:cr-id (:contractor-registration/id registration)}])

(defmethod entity-route :entity.type/complaint [complaint]
  ;; Complaints have no page of their own, they are opened in a modal on Search.
  [:view/search nil {:selected-tab  "complaint"
                     :complaint-ref (:complaint/id complaint)}])

(defmethod entity-route :entity.type/inspection [inspection]
  (when-some [workspace-id (-> inspection inspection-model/workspace :workspace/id)]
    [:view/inspection-visit {:workspace-id  workspace-id
                             :inspection-id (:db/id inspection)}]))

(defmethod entity-route :entity.type/property [property]
  [:view/property {:db-id (:db/id property)}])

(defn- entity-href
  "Link to the page of a visited entity, for the entity types that have one.

  Built with `href/branded-href` rather than `href/href`, because the rows are
  put together in a subscription."
  [branding entity]
  (some->> (entity-route entity)
           (apply href/branded-href branding)))

(defn- visit->row [{:keys [tr branding] :as opts} context {:keys [entity-id last-visited-at workspace-status]}]
  (let [entity (entities/find-entity context entity-id)]
    ;; The entity may be missing from the entity db when the backend left it out,
    ;; e.g. because it was deleted after the visit was recorded.
    (when (:entity/type entity)
      (merge {:db/id            entity-id
              :entity-type      (:entity/type entity)
              :last-visited-at  last-visited-at
              ;; Response metadata is replaced on every fetch, unlike merged entity attributes.
              :workspace-status workspace-status
              :href             (entity-href branding entity)
              :open-tr-key      (entity-open-tr-key entity)
              :kind             (some-> entity :entity/type entity-type->kind vector tr)}
             (entity-labels opts entity)))))

(rf/reg-sub ::rows
  :<- [::visits]
  :<- [::entities/context]
  :<- [:tr]
  :<- [:language]
  :<- [:branding/as-string]
  (fn [[visits context tr lang branding] _]
    ;; Sorted here rather than with the table's :initial-sorting, which always
    ;; compares columns as strings and so cannot order the rendered "x ago" cells.
    (->> visits
         (keep (partial visit->row {:tr tr :lang lang :branding branding} context))
         (sort-by :last-visited-at #(compare %2 %1))
         vec)))

(defn- name-cell [{:keys [href] row-name :name}]
  (if href
    (cc/link {:href href} row-name)
    row-name))

(defn- last-viewed-cell [{:keys [last-visited-at]}]
  [ui-updated/updated {:ts          last-visited-at
                       :verb-tr-key :recently-viewed/viewed}])

(def ^:private cols
  [[:recently-viewed/name name-cell {:sorting #(compare (:name %1) (:name %2))}]
   [:recently-viewed/description :description {:sorting true}]
   [:recently-viewed/address :address {:sorting true}]
   [:recently-viewed/type :kind {:sorting true}]
   [:recently-viewed/last-viewed last-viewed-cell
    {:sorting #(compare (:last-visited-at %1) (:last-visited-at %2))}]])

(defn- list-results [tr rows]
  [sortable-table/table
   {:id             "recently-viewed"
    :cols           cols
    :items          rows
    :tr             tr
    :sticky-header? true}])

(defn- open-link [tr {:keys [href open-tr-key]}]
  (cc/link
    {:href       href
     :data-cy    "recently-viewed-open-btn"
     :class-name (str "flex h-6 rounded items-center bg-ui-violet-100 text-white text-center font-bold "
                      "hover:bg-ui-violet-130 hover:no-underline")}
    [:div.flex-1.font-normal.text-white.uppercase (tr [open-tr-key])]))

(defn- visit-card [tr {:keys [description address kind href entity-type workspace-status] row-name :name :as row}]
  (cc/tile
    {:class-name "search-result-box rounded"}
    [:div.content-wrapper.flex.flex-col.gap-4.rounded
     (if href
       (cc/link {:href href :class-name "font-bold"} row-name)
       [:span.font-bold row-name])
     (when description
       [:div description])
     [:div.grow (when address address)]
     (when (and (ff/enabled? :feature-flag.id/closure-category-visibility) workspace-status)
       [:div.text-sm.text-gray-600 {:data-cy "recently-viewed-workspace-state"}
        (ws-util/status-text (ws-util/workspace-state tr workspace-status) workspace-status)])
     [:div.flex.items-center.gap-2
      (when-some [icon (entity-type->icon entity-type)]
        [:> icon])
      kind]
     (when href
       [open-link tr row])]))

(defn grid-results [tr rows]
  (into [:div.grid.grid-cols-1.md:grid-cols-2.lg:grid-cols-3.gap-4]
        (map (fn [{:db/keys [id] :as row}]
               ^{:key id} [visit-card tr row]))
        rows))

(defn view [_match]
  (r/with-let [*selected-tab (r/atom :dashboard-search/list-view)]
    (let [tr   (<sub [:tr])
          rows (<sub [::rows])]
      [:div.cloudpermit-page
       [:h1.mb-4.font-semibold (tr [:workspace-search/recently-viewed])]
       [:div.bg-white.p-4
        [:div.mb-4.flex.flex-wrap.items-center.gap-4
         (when (seq rows)
           [:p.p-0 (tr [:recently-viewed/showing-last] [(count rows)])])
         [:div.ml-auto
          [ui-rts/results-tab-switcher
           {:tr           tr
            :tabs         [ui-rts/list-tab ui-rts/grid-tab]
            :selected-tab @*selected-tab
            :on-select    #(reset! *selected-tab %)}]]]
        (if (seq rows)
          (case @*selected-tab
            :dashboard-search/grid-view [grid-results tr rows]
            [list-results tr rows])
          [:p (tr [:dashboard-search/no-results])])]])))

(def route
  {:name        :view/recently-viewed
   :view        view
   :controllers [{:start (fn [_]
                           (>evt [::fetch]))}]})
